import {useMemo, useState} from 'react'
import {SmallText} from '@/components/shared/SmallText'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {Plus, Play} from 'lucide-react'
import {useNavigate} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {Button} from '@/components/ui/button'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {FormField} from '@/components/shared/FormField'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {useAuth} from '@/contexts/auth-context'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useToast} from '@/hooks/use-toast'
import {routes} from '@/lib/routes'
import {permissions} from '@/lib/permissions'
import {formatDate} from '@/lib/date-utils'
import {
    CertificationCampaignStatus,
    CertificationCampaignStatusBadge,
} from '@/types/certification'
import type {CreateCampaignRequest} from '@/types/certification'
import {CertificationCampaignControllerCampaignsEndpoint} from '@/types/endpoint-contracts'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {CampaignFormFields, isCampaignFormValid} from '@/features/certification/components/CampaignFormFields'

interface CertificationCampaignsProps {
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

export function CertificationCampaigns({embedded, ownedByMe, emptyStateTitle}: CertificationCampaignsProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.certificationCampaigns.pageTitle)
    const navigate = useNavigate()
    const {toast} = useToast()
    const {user} = useAuth()

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: CertificationCampaignControllerCampaignsEndpoint,
        sortable: true,
        defaultSortBy: CertificationCampaignControllerCampaignsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const {listParams, canCreate, title, emptyMessage} = useEmbeddedListProps({
        contract: CertificationCampaignControllerCampaignsEndpoint,
        ownedByMe,
        baseQueryString: queryString,
        emptyStateTitle,
        defaultTitle: i18n.certificationCampaigns.allCampaigns,
        createPermission: permissions.certifications.create,
    })

    const createDialog = useDialog()
    const generateDialog = useDialog()
    const [newCampaign, setNewCampaign] = useState<CreateCampaignRequest>({
        name: '',
        description: null,
        dueDate: '',
        ownerPartyId: user?.partyId ?? '',
    })
    const [ownerSearchQuery, setOwnerSearchQuery] = useState('')

    // Resolve selected owner identity for display
    const {data: selectedOwner} = useQuery({
        queryKey: ['identities', newCampaign.ownerPartyId],
        queryFn: () => services.identities.detail(newCampaign.ownerPartyId),
        enabled: !!newCampaign.ownerPartyId,
    })

    // Search identities for owner selector
    const {data: ownerSearchData, isLoading: ownerSearchLoading} = useQuery({
        queryKey: ['identities', 'search', ownerSearchQuery],

        queryFn: () => services.identities.list({q: ownerSearchQuery, size: 10}),

    })
    const ownerOptions: ComboboxOption[] = (ownerSearchData?.content || []).map((i) => ({
        value: i.id,
        label: i.party.displayName || i.identityType,
        description: i.identityType,
    }))

    const [policySearchQuery, setPolicySearchQuery] = useState('')
    const {data: policySearchData, isLoading: policySearchLoading} = useQuery({
        queryKey: ['certification-policies', 'search', policySearchQuery],
        queryFn: () => services.certificationPolicies.list({q: policySearchQuery, size: 20}),
    })
    const policyList = Array.isArray(policySearchData) ? policySearchData : (policySearchData?.content ?? [])
    const policyOptions: ComboboxOption[] = policyList.map((p) => ({
        value: p.id,
        label: p.name,
        description: p.description ?? undefined,
    }))
    const {data: selectedPolicy} = useQuery({
        queryKey: ['certification-policy', newCampaign.certificationPolicyId],
        queryFn: () => services.certificationPolicies.detail(newCampaign.certificationPolicyId as string),
        enabled: !!newCampaign.certificationPolicyId,
    })

    const {data: campaignsPage} = useQuery({
        queryKey: ['certification-campaigns', queryString, page, pageSize, ownedByMe],
        queryFn: () => services.certification.listCampaigns(listParams),
    })

    const campaigns = campaignsPage?.content ?? []
    const totalItems = campaignsPage?.totalElements ?? 0
    const totalPages = campaignsPage?.totalPages ?? 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const createMutation = useEntityMutation({
        mutationFn: (request: CreateCampaignRequest) =>
            services.certificationCampaigns.create(request),
        invalidate: ['certification-campaigns'],
        closeDialog: () => createDialog.close(),
        onSuccess: (campaign) => {
            toast({description: i18n.certificationCampaigns.createSuccess({name: campaign.name})})
            navigate(routes.accessGovernance.certificationCampaigns.detail(campaign.id))
        },
    })

    const generateMutation = useEntityMutation({
        mutationFn: () => services.certification.generateCampaign({ownerPartyId: user?.partyId}),
        invalidate: ['certification-campaigns'],
        onSuccess: (result) => {
            toast({
                description: result.campaigns.length > 0
                    ? i18n.certificationCampaigns.generateSuccess({count: result.campaigns.length})
                    : i18n.certificationCampaigns.generateNoCampaigns,
            })
        },
    })

    const statusLabel = (status: CertificationCampaignStatus) => {
        switch (status) {
            case CertificationCampaignStatus.STAGING:
                return i18n.certificationCampaigns.statusStaging
            case CertificationCampaignStatus.BEFORE_GENERATION:
                return i18n.certificationCampaigns.statusBeforeGeneration
            case CertificationCampaignStatus.IN_GENERATION:
                return i18n.certificationCampaigns.statusInGeneration
            case CertificationCampaignStatus.STARTED:
                return i18n.certificationCampaigns.statusStarted
            case CertificationCampaignStatus.COMPLETED:
                return i18n.certificationCampaigns.statusCompleted
            case CertificationCampaignStatus.CANCELLED:
                return i18n.certificationCampaigns.statusCancelled
            default:
                return status
        }
    }

    return (
        <>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.certificationCampaign}
                title={title}
                count={totalItems}
                description={`${i18n.certificationCampaigns.campaignsCount({count: totalItems})}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
                data-testid="certification-campaigns-list"
                embedded={embedded}
                actions={canCreate ? (
                    <>
                        <Button
                            variant="outline"
                            onClick={generateDialog.open}
                            disabled={generateMutation.isPending || !user?.partyId}
                            data-testid="btn-generate-campaigns"
                        >
                            <Icon icon={Play} size="md" className="mr-2"/>
                            {i18n.certificationCampaigns.generate}
                        </Button>
                        <Button onClick={createDialog.open} data-testid="btn-create-campaign">
                            <Icon icon={Plus} size="md" className="mr-2"/>
                            {i18n.certificationCampaigns.createCampaign}
                        </Button>
                    </>
                ) : undefined}
                filters={
                    <UnifiedSearchBar
                        contract={CertificationCampaignControllerCampaignsEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.certificationCampaigns.searchByName}
                    />
                }
                pagination={totalItems > 0 ? (
                    <Pagination page={page} pageSize={pageSize} totalItems={totalItems} totalPages={totalPages} onPageChange={setPage} onPageSizeChange={setPageSize} pageSizeOptions={pageSizeOptions}/>
                ) : undefined}
            >
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead data-testid="col-name">{i18n.certificationCampaigns.name}</TableHead>
                            <TableHead data-testid="col-policy">{i18n.certificationCampaigns.policy}</TableHead>
                            <TableHead data-testid="col-status">{i18n.certificationCampaigns.status}</TableHead>
                            <TableHead data-testid="col-owner">{i18n.certificationCampaigns.owner}</TableHead>
                            <TableHead data-testid="col-due-date">{i18n.certificationCampaigns.dueDate}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {campaigns.map((campaign) => (
                            <ClickableTableRow
                                key={campaign.id}
                                to={routes.accessGovernance.certificationCampaigns.detail(campaign.id)}
                            >
                                <TableCell className="font-medium">
                                    <TextLink
                                        to={routes.accessGovernance.certificationCampaigns.detail(campaign.id)}
                                        stopPropagation
                                        testId={`link-campaign-${campaign.id}`}
                                    >
                                        <span data-testid={`campaign-name-${campaign.id}`}>{campaign.name}</span>
                                    </TextLink>
                                    {campaign.description && (
                                        <MutedText size="sm" className="block">
                                            {campaign.description}
                                        </MutedText>
                                    )}
                                </TableCell>
                                <TableCell data-testid={`campaign-policy-${campaign.id}`}>
                                    {campaign.policy ? (
                                        <TextLink
                                            to={routes.accessGovernance.certificationPolicies.detail(campaign.policy.id)}
                                            stopPropagation
                                            testId={`campaign-policy-link-${campaign.id}`}
                                        >
                                            {campaign.policy.displayName}
                                        </TextLink>
                                    ) : '—'}
                                </TableCell>
                                <TableCell>
                                    {(() => {
                                        const campaignBadge = CertificationCampaignStatusBadge.of(campaign.status)
                                        const CampaignIcon = campaignBadge?.icon
                                        return (
                                            <Badge variant={campaignBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1" data-testid={`campaign-status-${campaign.id}`}>
                                                {CampaignIcon && <CampaignIcon className="h-3 w-3" aria-hidden="true"/>}
                                                {statusLabel(campaign.status)}
                                            </Badge>
                                        )
                                    })()}
                                </TableCell>
                                <TableCell data-testid={`campaign-owner-${campaign.id}`}>{campaign.owners[0]?.ownerPartyName ?? '—'}</TableCell>
                                <TableCell>{formatDate(campaign.dueDate)}</TableCell>
                            </ClickableTableRow>
                        ))}
                        {campaigns.length === 0 && (
                            <TableEmptyState
                                colSpan={5}
                                hasFilters={hasActiveFilters}
                                matchMessage={i18n.certificationCampaigns.noCampaignsFound}
                                emptyMessage={emptyMessage ?? i18n.certificationCampaigns.noCampaignsFound}
                            />
                        )}
                    </TableBody>
                </Table>
            </PageLayout>

            <Dialog open={createDialog.isOpen} onOpenChange={createDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.createCampaign}</DialogTitle>
                        <DialogDescription>{i18n.certificationCampaigns.createDescription}</DialogDescription>
                    </DialogHeader>
                    <FormField label={String(i18n.certificationCampaigns.policy)} htmlFor="campaign-combobox-policy">
                        <Combobox
                            options={policyOptions}
                            value={newCampaign.certificationPolicyId ?? null}
                            onValueChange={(v) => setNewCampaign({...newCampaign, certificationPolicyId: v ?? undefined})}
                            onSearch={setPolicySearchQuery}
                            placeholder={i18n.certificationCampaigns.selectPolicy}
                            emptyMessage={i18n.certificationCampaigns.noPoliciesFound}
                            isLoading={policySearchLoading}
                            selectedLabel={selectedPolicy?.name}
                            data-testid="campaign-combobox-policy"
                        />
                    </FormField>
                    <CampaignFormFields
                        value={newCampaign}
                        onChange={setNewCampaign}
                        ownerOptions={ownerOptions}
                        onOwnerSearch={setOwnerSearchQuery}
                        ownerSearchLoading={ownerSearchLoading}
                        selectedOwnerLabel={selectedOwner?.party?.displayName || selectedOwner?.identityType}
                    />
                    <SaveCancelFooter
                        testIdPrefix="create"
                        onCancel={createDialog.close}
                        saving={createMutation.isPending}
                        disabled={!isCampaignFormValid(newCampaign) || !newCampaign.certificationPolicyId}
                        saveLabel={i18n.certificationCampaigns.createCampaign}
                        saveType="button"
                        onSave={() => createMutation.mutate(newCampaign)}
                    />
                </DialogContent>
            </Dialog>

            <Dialog open={generateDialog.isOpen} onOpenChange={generateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.certificationCampaigns.generate}</DialogTitle>
                        <DialogDescription>{i18n.certificationCampaigns.generateDescription}</DialogDescription>
                    </DialogHeader>
                    <SmallText className="py-2"><MutedText>{i18n.certificationCampaigns.generateExplanation}</MutedText></SmallText>
                    <SaveCancelFooter
                        testIdPrefix="generate"
                        onCancel={generateDialog.close}
                        saving={generateMutation.isPending}
                        saveLabel={i18n.certificationCampaigns.generate}
                        saveType="button"
                        onSave={() => {
                            generateMutation.mutate()
                            generateDialog.close()
                        }}
                    />
                </DialogContent>
            </Dialog>
        </>
    )
}
